import { useState } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, EstadoVacio } from '@/components/Card';
import { Falta } from '@/components/Falta';
import { TextField, NumberField, MoneyField } from '@/components/Field';
import { SelloConfianza } from './SelloConfianza';
import { listarRecetas, costoPorPorcionDeTodas, crearReceta } from '@/db/repos/recipes';
import { formatearCentavos, textoACentavos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';

/**
 * Lista de recetas con su costo por porcion.
 *
 * El costo se calcula al momento con los costos vigentes del inventario, asi
 * que esta lista se actualiza sola cuando se registra una compra.
 */
export function RecipesPage() {
  const recetas = useLiveQuery(() => listarRecetas(), []);
  const costos = useLiveQuery(() => costoPorPorcionDeTodas(), []);
  const [creando, setCreando] = useState(false);

  if (creando) {
    return <NuevaRecetaForm onListo={() => setCreando(false)} onCancelar={() => setCreando(false)} />;
  }

  return (
    <div className="space-y-5">
      <PageHeader titulo="Recetas" descripcion="Cuánto cuesta cada platillo y cuánto deja" />

      <Button tamano="grande" onClick={() => setCreando(true)}>
        + Nueva receta
      </Button>

      {recetas === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

      {recetas !== undefined && recetas.length === 0 && (
        <EstadoVacio
          titulo="Todavía no hay recetas"
          descripcion="Crea la primera con su nombre y cuántas porciones rinde. Los ingredientes se agregan después."
        />
      )}

      {recetas !== undefined && recetas.length > 0 && (
        <ul className="space-y-3">
          {recetas.map((receta) => {
            const costo = costos?.get(receta.id);

            return (
              <li key={receta.id}>
                <Link to={`/recetas/${receta.id}`} className="block">
                  <Card className="active:bg-superficie-suave">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-texto">{receta.nombre}</p>
                        <p className="text-sm text-texto-suave">
                          Rinde {receta.rendimientoPorciones}{' '}
                          {receta.rendimientoPorciones === 1 ? 'porción' : 'porciones'}
                          {receta.precioVentaCentavos > 0 && (
                            <> · se vende a {formatearCentavos(receta.precioVentaCentavos)}</>
                          )}
                        </p>
                      </div>

                      <div className="shrink-0 text-right">
                        {costo === undefined ? (
                          <span className="text-sm text-texto-tenue">—</span>
                        ) : (
                          <>
                            <p className="text-lg font-bold tabular-nums text-texto">
                              {formatearCentavos(costo.centavos)}
                            </p>
                            <p className="text-xs text-texto-tenue">por porción</p>
                          </>
                        )}
                      </div>
                    </div>

                    {costo !== undefined && costo.confianza !== 'firme' && (
                      <div className="mt-2">
                        <SelloConfianza confianza={costo.confianza} />
                      </div>
                    )}
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsNueva {
  onListo: () => void;
  onCancelar: () => void;
}

/**
 * Alta de receta con lo minimo indispensable.
 *
 * El precio de venta y los dias de anticipacion son opcionales aqui porque no
 * hacen falta para costear. Se piden de una vez porque la Fase 4 los necesita y
 * asi no hay que volver a entrar a cada receta.
 */
function NuevaRecetaForm({ onListo, onCancelar }: PropsNueva) {
  const [nombre, setNombre] = useState('');
  const [porciones, setPorciones] = useState('');
  const [precio, setPrecio] = useState('');
  const [dias, setDias] = useState('1');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const porcionesNumero = Number(porciones);
  const diasNumero = Number(dias);

  const faltan: string[] = [];
  if (nombre.trim() === '') faltan.push('el nombre');
  if (!Number.isFinite(porcionesNumero) || porcionesNumero <= 0) {
    faltan.push('cuántas porciones rinde');
  }
  const valida = faltan.length === 0;

  async function guardar() {
    setGuardando(true);
    setError(null);

    try {
      await crearReceta({
        nombre,
        rendimientoPorciones: porcionesNumero,
        precioVentaCentavos: textoACentavos(precio) ?? 0,
        diasAnticipacion: Number.isFinite(diasNumero) ? diasNumero : 1,
      });
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader titulo="Nueva receta" descripcion="Los ingredientes se agregan después" />

      <TextField
        etiqueta="Nombre del platillo"
        valor={nombre}
        onCambio={setNombre}
        placeholder="Birria"
        autoFocus
        maxLength={80}
      />

      <NumberField
        etiqueta="Cuántas porciones rinde"
        valor={porciones}
        onCambio={setPorciones}
        placeholder="10"
        ayuda="La receta completa, tal como la cocinas normalmente"
        decimal={false}
      />

      <MoneyField
        etiqueta="Precio de venta por porción"
        valor={precio}
        onCambio={setPrecio}
        ayuda="Opcional. Si lo pones, la app calcula tu ganancia y tu margen."
      />

      <NumberField
        etiqueta="Días de anticipación"
        valor={dias}
        onCambio={setDias}
        ayuda="Cuántos días antes hay que cocinarla. Cero si se vende el mismo día."
        decimal={false}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar">
          {error}
        </Banner>
      )}

      <div className="space-y-2">
        <Button tamano="grande" disabled={guardando || !valida} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Crear receta'}
        </Button>
        <Falta faltan={faltan} />
        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
